import { useEffect, useState } from 'react';

export default function ScreenshotGallery({ name, screenshots }: { name: string; screenshots: string[] }) {
  const [index, setIndex] = useState<number | null>(null);

  useEffect(() => {
    if (index === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIndex(null);
      if (e.key === 'ArrowRight') setIndex((p) => (p === null ? p : (p + 1) % screenshots.length));
      if (e.key === 'ArrowLeft') setIndex((p) => (p === null ? p : (p - 1 + screenshots.length) % screenshots.length));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, screenshots.length]);

  return (
    <section>
      <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-foreground-950">
        <i className="ri-image-2-line text-primary-600"></i>软件截图
      </h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {screenshots.map((src, i) => (
          <button
            key={src}
            type="button"
            onClick={() => setIndex(i)}
            className="group relative cursor-pointer overflow-hidden rounded-lg border border-background-200 bg-background-50"
          >
            <div className="h-44 w-full overflow-hidden bg-secondary-100">
              <img
                src={src}
                alt={`${name} 界面截图 ${i + 1}`}
                title={`${name} - 看片软件界面预览`}
                className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <span className="absolute inset-0 flex items-center justify-center bg-primary-950/0 transition-colors group-hover:bg-primary-950/40">
              <i className="ri-zoom-in-line text-2xl text-background-50 opacity-0 transition-opacity group-hover:opacity-100"></i>
            </span>
          </button>
        ))}
      </div>

      {index !== null && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-primary-950/90 p-4"
          onClick={() => setIndex(null)}
        >
          <button
            type="button"
            aria-label="关闭预览"
            onClick={() => setIndex(null)}
            className="absolute right-5 top-5 flex h-10 w-10 cursor-pointer items-center justify-center rounded-md bg-background-50/20 text-background-50"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>

          <button
            type="button"
            aria-label="上一张"
            onClick={(e) => {
              e.stopPropagation();
              setIndex((p) => (p === null ? p : (p - 1 + screenshots.length) % screenshots.length));
            }}
            className="absolute left-4 flex h-11 w-11 cursor-pointer items-center justify-center rounded-full bg-background-50/20 text-background-50 md:left-8"
          >
            <i className="ri-arrow-left-s-line text-2xl"></i>
          </button>

          <figure className="max-h-[80vh] w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <img
              src={screenshots[index]}
              alt={`${name} 界面截图 ${index + 1}`}
              className="max-h-[76vh] w-full rounded-lg object-contain"
            />
            <figcaption className="mt-3 text-center text-sm text-background-100">
              {name} · 界面预览 {index + 1} / {screenshots.length}
            </figcaption>
          </figure>

          <button
            type="button"
            aria-label="下一张"
            onClick={(e) => {
              e.stopPropagation();
              setIndex((p) => (p === null ? p : (p + 1) % screenshots.length));
            }}
            className="absolute right-4 flex h-11 w-11 cursor-pointer items-center justify-center rounded-full bg-background-50/20 text-background-50 md:right-8"
          >
            <i className="ri-arrow-right-s-line text-2xl"></i>
          </button>
        </div>
      )}
    </section>
  );
}